import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Eye, Ear, Heart, Brain, Sparkles, TrendingUp, Target } from 'lucide-react';

interface PracticeSession {
  id: string;
  date: string;
  exercise_type: string;
  clair_type: string;
  accuracy: number;
  confidence_level: number;
  notes: string;
  insights: string;
  duration_minutes: number;
}

const clairTypes = [
  { value: 'clairvoyance', label: 'Clairvoyance (Clear Seeing)', icon: Eye, description: 'Visual psychic impressions' },
  { value: 'clairaudience', label: 'Clairaudience (Clear Hearing)', icon: Ear, description: 'Auditory psychic impressions' },
  { value: 'clairsentience', label: 'Clairsentience (Clear Feeling)', icon: Heart, description: 'Emotional/physical sensations' },
  { value: 'claircognizance', label: 'Claircognizance (Clear Knowing)', icon: Brain, description: 'Intuitive knowledge' },
  { value: 'clairalience', label: 'Clairalience (Clear Smelling)', icon: Sparkles, description: 'Psychic smell' },
  { value: 'clairgustance', label: 'Clairgustance (Clear Tasting)', icon: Sparkles, description: 'Psychic taste' }
];

const exercises = {
  clairvoyance: [
    'Remote viewing practice with hidden objects',
    'Aura color perception',
    'Symbol flash card practice',
    'Third eye meditation'
  ],
  clairaudience: [
    'Spirit guide communication',
    'Telepathic message reception',
    'Psychic hearing in meditation',
    'Sound frequency attunement'
  ],
  clairsentience: [
    'Emotion reading practice',
    'Object psychometry',
    'Energy field sensing',
    'Physical empathy exercises'
  ],
  claircognizance: [
    'Instant knowing exercises',
    'Prediction journaling',
    'Automatic writing',
    'Intuitive decision making'
  ],
  clairalience: [
    'Spirit presence through scent',
    'Memory-scent connections',
    'Phantom smell recognition'
  ],
  clairgustance: [
    'Taste perception meditation',
    'Food energy reading',
    'Spirit connection through taste'
  ]
};

export default function PsychicPracticeLab() {
  const [sessions, setSessions] = useState<PracticeSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    exercise_type: '',
    clair_type: '',
    accuracy: 50,
    confidence_level: 5,
    notes: '',
    insights: '',
    duration_minutes: 15
  });

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('psychic_practice_sessions')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load practice sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in');
        return;
      }

      const { error } = await supabase
        .from('psychic_practice_sessions')
        .insert({
          user_id: user.id,
          ...formData
        });

      if (error) throw error;

      toast.success('Practice session logged!');
      setShowForm(false);
      setFormData({
        exercise_type: '',
        clair_type: '',
        accuracy: 50,
        confidence_level: 5,
        notes: '',
        insights: '',
        duration_minutes: 15
      });
      loadSessions();
    } catch (error) {
      console.error('Error saving session:', error);
      toast.error('Failed to save practice session');
    }
  };

  const getDominantClair = () => {
    const clairCounts: Record<string, { count: number; avgAccuracy: number; totalAccuracy: number }> = {};
    
    sessions.forEach(session => {
      if (!clairCounts[session.clair_type]) {
        clairCounts[session.clair_type] = { count: 0, avgAccuracy: 0, totalAccuracy: 0 };
      }
      clairCounts[session.clair_type].count++;
      clairCounts[session.clair_type].totalAccuracy += session.accuracy;
    });

    Object.keys(clairCounts).forEach(clair => {
      clairCounts[clair].avgAccuracy = clairCounts[clair].totalAccuracy / clairCounts[clair].count;
    });

    const dominant = Object.entries(clairCounts).sort((a, b) => {
      const scoreA = a[1].avgAccuracy * a[1].count;
      const scoreB = b[1].avgAccuracy * b[1].count;
      return scoreB - scoreA;
    })[0];

    return dominant ? { type: dominant[0], ...dominant[1] } : null;
  };

  const getClairProgress = (clairType: string) => {
    const clairSessions = sessions.filter(s => s.clair_type === clairType);
    if (clairSessions.length === 0) return null;

    const recent = clairSessions.slice(0, 5);
    const older = clairSessions.slice(-5);
    
    const recentAvg = recent.reduce((sum, s) => sum + s.accuracy, 0) / recent.length;
    const olderAvg = older.reduce((sum, s) => sum + s.accuracy, 0) / older.length;
    
    const improvement = recentAvg - olderAvg;
    
    return {
      count: clairSessions.length,
      recentAvg: Math.round(recentAvg),
      improvement: Math.round(improvement)
    };
  };

  if (loading) {
    return <div className="text-muted-foreground">Loading practice lab...</div>;
  }

  const dominant = getDominantClair();

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-gradient-to-br from-secondary/10 to-accent/10 border-accent/20">
        <h2 className="text-2xl font-bold mb-2">Psychic Practice Lab</h2>
        <p className="text-muted-foreground mb-4">
          Develop and track your psychic abilities across all the clairs. Discover your dominant sense and watch your accuracy improve over time.
        </p>

        {dominant && (
          <div className="bg-background/50 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-5 w-5 text-accent" />
              <h3 className="font-semibold">Your Dominant Clair</h3>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="secondary" className="capitalize">{dominant.type}</Badge>
              <span className="text-sm text-muted-foreground">
                {dominant.count} sessions • {Math.round(dominant.avgAccuracy)}% avg accuracy
              </span>
            </div>
          </div>
        )}

        <Button onClick={() => setShowForm(!showForm)} className="w-full bg-gradient-to-r from-secondary to-accent hover:opacity-90">
          {showForm ? 'Cancel' : 'Log Practice Session'}
        </Button>
      </Card>

      {showForm && (
        <Card className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Clair Type</Label>
              <Select value={formData.clair_type} onValueChange={(value) => setFormData({ ...formData, clair_type: value, exercise_type: '' })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your psychic sense" />
                </SelectTrigger>
                <SelectContent>
                  {clairTypes.map(clair => (
                    <SelectItem key={clair.value} value={clair.value}>
                      <div className="flex items-center gap-2">
                        <clair.icon className="h-4 w-4" />
                        <span>{clair.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.clair_type && (
                <p className="text-xs text-muted-foreground mt-1">
                  {clairTypes.find(c => c.value === formData.clair_type)?.description}
                </p>
              )}
            </div>

            {formData.clair_type && (
              <div>
                <Label>Exercise Type</Label>
                <Select value={formData.exercise_type} onValueChange={(value) => setFormData({ ...formData, exercise_type: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select exercise" />
                  </SelectTrigger>
                  <SelectContent>
                    {exercises[formData.clair_type as keyof typeof exercises]?.map(exercise => (
                      <SelectItem key={exercise} value={exercise}>{exercise}</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom exercise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                min="1"
              />
            </div>

            <div>
              <Label>Accuracy: {formData.accuracy}%</Label>
              <Slider
                value={[formData.accuracy]}
                onValueChange={(value) => setFormData({ ...formData, accuracy: value[0] })}
                max={100}
                step={5}
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">How accurate were your impressions?</p>
            </div>

            <div>
              <Label>Confidence Level: {formData.confidence_level}/10</Label>
              <Slider
                value={[formData.confidence_level]}
                onValueChange={(value) => setFormData({ ...formData, confidence_level: value[0] })}
                max={10}
                min={1}
                step={1}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="What did you practice? What came through?"
                rows={3}
              />
            </div>

            <div>
              <Label>Insights & Observations</Label>
              <Textarea
                value={formData.insights}
                onChange={(e) => setFormData({ ...formData, insights: e.target.value })}
                placeholder="What did you learn? Any patterns or breakthroughs?"
                rows={3}
              />
            </div>

            <Button type="submit" className="w-full bg-gradient-to-r from-secondary to-accent hover:opacity-90">Save Practice Session</Button>
          </form>
        </Card>
      )}

      {/* Progress by Clair Type */}
      <Card className="p-6">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Your Progress by Clair Type
        </h3>
        <div className="space-y-3">
          {clairTypes.map(clair => {
            const progress = getClairProgress(clair.value);
            const Icon = clair.icon;
            
            return (
              <div key={clair.value} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-accent" />
                  <div>
                    <p className="font-medium text-sm">{clair.label}</p>
                    {progress && (
                      <p className="text-xs text-muted-foreground">
                        {progress.count} sessions • Recent avg: {progress.recentAvg}%
                      </p>
                    )}
                  </div>
                </div>
                {progress && progress.improvement > 0 && (
                  <Badge variant="secondary" className="bg-green-500/20 text-green-400">
                    +{progress.improvement}%
                  </Badge>
                )}
                {!progress && (
                  <span className="text-xs text-muted-foreground">No data yet</span>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Recent Sessions */}
      <div className="space-y-3">
        <h3 className="font-semibold">Recent Practice Sessions</h3>
        {sessions.length === 0 ? (
          <Card className="p-6 text-center text-muted-foreground">
            <p>No practice sessions logged yet. Start practicing to track your progress!</p>
          </Card>
        ) : (
          sessions.slice(0, 10).map(session => {
            const clairInfo = clairTypes.find(c => c.value === session.clair_type);
            const Icon = clairInfo?.icon || Sparkles;
            
            return (
              <Card key={session.id} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon className="h-5 w-5 text-accent" />
                    <div>
                      <p className="font-medium">{session.exercise_type}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(session.date).toLocaleDateString()} • {session.duration_minutes} min
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="secondary">{session.accuracy}% accurate</Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      Confidence: {session.confidence_level}/10
                    </p>
                  </div>
                </div>
                {session.notes && (
                  <p className="text-sm text-muted-foreground mt-2">{session.notes}</p>
                )}
                {session.insights && (
                  <p className="text-sm mt-2 p-2 bg-primary/10 rounded">
                    💡 {session.insights}
                  </p>
                )}
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
}